import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashStyleValue, SeriesOptionsType } from 'highcharts';
import { Card, Space, Typography } from 'antd';
import { useTheme } from '../../../../../../../providers/theme';
import { getColors } from '../../../../../../../components/utils/getColors';
import { useSelected } from '../../../../../../../providers/selectedContext';
import ChartSettings from '../../../../../../../components/chart/settings';
import { ChartSettingsProps } from '../../../../../../../@types/data/types';
import { TCorrelationValue } from '../../../../../types';
import { buildAnalysisTypeLegendSeries } from '../analysisTypeLegend';
import { Chart } from '../../../../../../../components/chart/BaseChart';
import { ChartSpec } from '../../../../../../../components/chart/chartSpec';
import { CHART_BOOST_ENABLED, EXPERIMENT_BOOST } from '../../../../../../../components/chart/boost';

export const FZI_LINES = [
  { label: 'FZI0', fzi: 0.35 },
  { label: 'FZI1', fzi: 0.85 },
  { label: 'FZI2', fzi: 1.55 },
  { label: 'FZI3', fzi: 2.07 },
  { label: 'FZI4', fzi: 2.75 },
  { label: 'FZI5', fzi: 4.00 },
  { label: 'FZI6', fzi: 10.40 },
];

const FZI_LINE_COLORS = [
  '#e41a1c', '#377eb8', '#4daf4a', '#984ea3',
  '#ff7f00', '#a65628', '#f781bf',
];

const makeFziLineSeries = (label: string, fzi: number, color: string): SeriesOptionsType => ({
  id: `fzi-line-${label}`,
  name: label,
  type: 'line',
  data: [[0.01, fzi * 0.01], [1, fzi]],
  color,
  marker: { enabled: false },
  dashStyle: 'ShortDash' as DashStyleValue,
  enableMouseTracking: true,
  showInLegend: false,
  zIndex: 1,
  tooltip: {
    headerFormat: '',
    pointFormat: `<b>${label}</b> (FZI=${fzi})<br/>PHIZ: {point.x:.4f}<br/>RQI: {point.y:.4f}`
  }
});

interface RqiPhizChartProps {
  permData: TCorrelationValue[];
  containerWidth?: string | number;
  height?: string | number;
}

const RqiPhizChart: React.FC<RqiPhizChartProps> = ({ permData, containerWidth, height }) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo } = useSelected();
  const chartRef = useRef<any>(null);

  const chartZones = useMemo(() => {
    const zones = selectedInfo.project?.correlation?.zones;
    return zones?.map((zone: any, index: number) => ({
      ...zone,
      name: 'Rock Type ' + (index + 1)
    })) || [];
  }, [selectedInfo.project?.correlation?.zones]);

  const scatterSeries = useMemo((): SeriesOptionsType[] => {
    const validPoints = permData.filter(
      (p) => p.phiz != null && p.phiz > 0 && p.rqi != null && p.rqi > 0
    );

    const byZone = chartZones.map((zone: any) => {
      const pointsInZone = validPoints.filter(
        (p) => (p.xAxisValue ?? 0) >= zone.start && (p.xAxisValue ?? 0) <= zone.end
      );
      return {
        id: `rqi-zone-${zone.name}`,
        name: zone.name,
        type: 'scatter' as const,
        data: pointsInZone.map((p) => ({ x: p.phiz!, y: p.rqi!, name: p.name, marker: { symbol: p.marker?.symbol } })),
        color: zone.color ?? undefined,
        marker: { symbol: 'circle' },
        zIndex: 3,
        tooltip: {
          headerFormat: '<b>{series.name}</b><br/>',
          pointFormat: 'Sample: {point.name}<br/>PHIZ: {point.x:.4f}<br/>RQI: {point.y:.4f}'
        }
      } as SeriesOptionsType;
    });

    const outsideZone = validPoints.filter(
      (p) => !chartZones.some((z: any) => (p.xAxisValue ?? 0) >= z.start && (p.xAxisValue ?? 0) <= z.end)
    );

    byZone.push({
      id: 'rqi-no-zone',
      name: 'No rock type',
      type: 'scatter' as const,
      data: outsideZone.map((p) => ({ x: p.phiz!, y: p.rqi!, color: p.color ?? '#808080', name: p.name, marker: { symbol: p.marker?.symbol } })),
      color: '#808080',
      marker: { symbol: 'circle' },
      zIndex: 3,
      tooltip: {
        headerFormat: '<b>{series.name}</b><br/>',
        pointFormat: 'Sample: {point.name}<br/>PHIZ: {point.x:.4f}<br/>RQI: {point.y:.4f}'
      }
    } as SeriesOptionsType);

    return byZone;
  }, [permData, chartZones]);

  const fziLineSeries = useMemo((): SeriesOptionsType[] =>
    FZI_LINES.map(({ label, fzi }, i) => makeFziLineSeries(label, fzi, FZI_LINE_COLORS[i % FZI_LINE_COLORS.length])),
    []
  );

  const analysisLegendSeries = useMemo(
    () => buildAnalysisTypeLegendSeries(permData, colors.text),
    [permData, colors.text]
  );

  const autoDefaults = useMemo(() => {
    const validPoints = permData.filter((p) => p.phiz != null && p.phiz > 0 && p.rqi != null && p.rqi > 0);
    const xVals = validPoints.map((p) => p.phiz!).filter(Number.isFinite);
    const yVals = validPoints.map((p) => p.rqi!).filter(Number.isFinite);
    return {
      xmin: xVals.length > 0 ? Math.min(...xVals) : 0.01,
      xmax: xVals.length > 0 ? Math.max(...xVals) : 1,
      ymin: yVals.length > 0 ? Math.min(...yVals) : 0.001,
      ymax: yVals.length > 0 ? Math.max(...yVals) : 10,
    };
  }, [permData]);

  const [chartSettings, setChartSettings] = useState<ChartSettingsProps>({
    xmin: autoDefaults.xmin,
    xmax: autoDefaults.xmax,
    ymin: autoDefaults.ymin,
    ymax: autoDefaults.ymax,
  });
  const [userHasSetLimits, setUserHasSetLimits] = useState(false);

  useEffect(() => {
    if (!userHasSetLimits) setChartSettings(autoDefaults);
  }, [autoDefaults, userHasSetLimits]);

  const handleSetChartSettings = useCallback((s: ChartSettingsProps) => {
    setChartSettings(s);
    setUserHasSetLimits(true);
  }, []);

  const spec: ChartSpec = useMemo(() => {
    const safeYMin = (chartSettings.ymin != null && chartSettings.ymin > 0)
      ? chartSettings.ymin
      : (autoDefaults.ymin > 0 ? autoDefaults.ymin : 0.001);

    return {
      zoomType: 'xy',
      height,
      title: 'RQI vs PHIZ',
      xAxis: {
        type: 'logarithmic',
        title: 'PHIZ = φ/(1–φ)',
        min: chartSettings.xmin ?? autoDefaults.xmin,
        max: chartSettings.xmax ?? autoDefaults.xmax,
      },
      yAxis: {
        type: 'logarithmic',
        title: 'RQI (μm)',
        min: safeYMin,
        max: chartSettings.ymax ?? autoDefaults.ymax,
        minorTickInterval: 0.5,
        tickInterval: 1,
      },
      plotOptions: {
        scatter: {
          marker: {
            radius: 5,
            states: { hover: { enabled: true, lineColor: 'rgb(100,100,100)' } },
          },
        },
        line: {
          marker: { enabled: false },
        },
      },
      legend: true,
      boost: CHART_BOOST_ENABLED ? EXPERIMENT_BOOST : undefined,
      series: [...fziLineSeries, ...scatterSeries, ...analysisLegendSeries],
    };
  }, [height, chartSettings, autoDefaults, fziLineSeries, scatterSeries, analysisLegendSeries]);

  return (
    <Space
      direction="vertical"
      style={{
        backgroundColor: 'transparent',
        marginTop: '1rem',
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        transition: 'width 0.5s',
        width: containerWidth ?? '49%',
        overflow: 'hidden',
      }}
    >
      <Card size="small" style={{ backgroundColor: 'transparent', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Typography.Text strong style={{ color: colors.text }}>
            RQI vs PHIZ — FZI Zone Lines
          </Typography.Text>
          <ChartSettings
            chartSettings={chartSettings}
            setChartSettings={handleSetChartSettings}
            autoDefaults={autoDefaults}
          />
        </div>
        <div style={{ position: 'relative', borderRadius: 12, border: `1px solid ${colors.border}`, padding: 12 }}>
          <Chart spec={spec} chartRef={chartRef} />
        </div>
      </Card>
    </Space>
  );
};

export default RqiPhizChart;
